import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, CheckCircle, XCircle, Clock, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, failed, expired
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;
  const pollInterval = 2000;

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        setPaymentData(res.data);

        if (res.data.payment_status === "paid") {
          setStatus("success");
          return true;
        } else if (res.data.status === "expired") {
          setStatus("expired");
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error checking payment status:", error);
        if (attempts >= maxAttempts - 1) {
          setStatus("failed");
          return true;
        }
        return false;
      }
    };

    const poll = async () => {
      const done = await pollPaymentStatus();
      if (!done && attempts < maxAttempts) {
        setAttempts(prev => prev + 1);
        setTimeout(poll, pollInterval);
      }
    };

    poll();
  }, [sessionId]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center px-4">
        <Card className="bg-white max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
            <h1 className="text-xl font-bold text-foreground mb-2">Processing Payment</h1>
            <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
            <Skeleton className="h-4 w-48 mx-auto mt-4" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center px-4">
        <Card className="bg-white max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Payment Successful!</h1>
            <p className="text-muted-foreground mb-6">
              Thank you for your payment. A confirmation email has been sent.
            </p>
            
            {paymentData && (
              <div className="bg-green-50 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-muted-foreground">Amount Paid</span>
                  <span className="font-semibold font-mono">
                    ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                  </span>
                </div>
                {paymentData.metadata?.invoice_number && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Invoice</span>
                    <span className="font-semibold">#{paymentData.metadata.invoice_number}</span>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3">
              {paymentData?.metadata?.invoice_id && (
                <Button asChild className="w-full">
                  <Link to={`/invoice/view/${paymentData.metadata.invoice_id}`}>
                    <FileText className="w-4 h-4 mr-2" />
                    View Invoice
                  </Link>
                </Button>
              )}
              <Button variant="outline" asChild className="w-full">
                <Link to="/">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Billuna
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "expired") {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center px-4">
        <Card className="bg-white max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-orange-600" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Session Expired</h1>
            <p className="text-muted-foreground mb-6">
              Your payment session has expired. Please try again.
            </p>
            <Button variant="outline" asChild className="w-full">
              <Link to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Billuna
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Failed status
  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center px-4">
      <Card className="bg-white max-w-md w-full">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Payment Issue</h1>
          <p className="text-muted-foreground mb-6">
            We couldn't confirm your payment. Please check your email for confirmation or contact support.
          </p>
          <Button variant="outline" asChild className="w-full">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Billuna
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
